import React from 'react';
import { getEnv } from 'src/env';
import { getAttachmentKind, canOpenInNewTab, formatFileSize, getExtensionLabel, KIND_ICONS } from 'src/utils/attachmentType';

const MEDIA_BASE_URL = getEnv('REACT_APP_SERVER_URL');

/**
 * Спільна міні-галерея мініатюр вкладень листа.
 * За замовчуванням read-only; хрестик видалення з'являється, тільки якщо передано onDelete.
 * Зображення рендеряться як <img>, решта типів — плиткою з іконкою і розширенням.
 *
 * @param {Array<{media_id: number, name?: string, content_url: string, download_url?: string, file_size?: number}>} attachments
 * @param {function} onSelect - callback(index) при кліку по мініатюрі зображення/відео
 * @param {function} [onDelete] - callback(media_id, name) при кліку на хрестик; якщо не передано — хрестика немає
 * @param {boolean} [disabled] - дизейблить кнопку видалення і завантаження (наприклад, під час запиту)
 * @param {number} [size] - сторона мініатюри в px
 */
const MediaThumbGallery = ({ attachments = [], onSelect, onDelete, disabled = false, size = 80 }) => {
	if (!attachments || attachments.length === 0) return null;

	const handleTileClick = (att, idx, kind) => {
		if (kind === 'image' || kind === 'video') {
			onSelect?.(idx);
		} else if (kind === 'pdf' || kind === 'text') {
			window.open(MEDIA_BASE_URL + att.content_url, '_blank', 'noopener');
		} else {
			window.location.href = MEDIA_BASE_URL + att.download_url;
		}
	};

	return (
		<div className="media-thumb-gallery">
			{attachments.map((att, idx) => {
				const kind = getAttachmentKind(att.name, att.content_url);
				const sizeLabel = formatFileSize(att.file_size);
				return (
					<div key={att.media_id} className="media-thumb-gallery__item" style={{ width: size }}>
						<div className="media-thumb-gallery__image-wrap">
							{kind === 'image' ? (
								<img
									src={MEDIA_BASE_URL + att.content_url}
									alt={att.name || ''}
									className="media-thumb-gallery__image"
									style={{ width: size, height: size }}
									onClick={() => handleTileClick(att, idx, kind)}
								/>
							) : (
								<div
									className="media-thumb-gallery__file-tile"
									style={{ width: size, height: size }}
									onClick={() => handleTileClick(att, idx, kind)}
									title={att.name || ''}
								>
									<span className="media-thumb-gallery__file-icon">{KIND_ICONS[kind]}</span>
									<span className="media-thumb-gallery__file-ext">{getExtensionLabel(att.name, att.content_url)}</span>
								</div>
							)}
							{kind !== 'image' && (
								<div className="media-thumb-gallery__actions">
									{canOpenInNewTab(kind) && (
										<button
											type="button"
											className="media-thumb-gallery__action-btn"
											onClick={() => window.open(MEDIA_BASE_URL + att.content_url, '_blank', 'noopener')}
											title="Відкрити в новій вкладці"
										>
											↗
										</button>
									)}
									<a
										href={MEDIA_BASE_URL + att.download_url}
										className={`media-thumb-gallery__action-btn${disabled ? ' media-thumb-gallery__action-btn--disabled' : ''}`}
										onClick={(e) => disabled && e.preventDefault()}
										title="Завантажити"
									>
										⬇
									</a>
								</div>
							)}
							{onDelete && (
								<button
									type="button"
									className="media-thumb-gallery__remove"
									onClick={() => onDelete(att.media_id, att.name)}
									disabled={disabled}
									title="Видалити вкладення"
								>
									<span>×</span>
								</button>
							)}
						</div>
						<div className="media-thumb-gallery__name text-truncate" title={att.name || ''}>
							{att.name || '—'}
						</div>
						{sizeLabel && (
							<div className="media-thumb-gallery__size text-truncate">
								{sizeLabel}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

export default MediaThumbGallery;
